import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Award,
  BookOpen,
  Briefcase,
  Building,
  Building2,
  Contact,
  LayoutGrid,
  LogOut,
  Menu,
  Newspaper,
  RefreshCw,
  Rss,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { logOutUser } from "../Firebase/Firebase.utils";
import { SERVICES } from "./utils/services";
import { SITE_PAGES } from "./utils/sitePages";
import { staffAuthHeaders } from "./utils/revalidatePublic";

// Fixed left sidebar for the CMS — replaces the previous horizontal
// CmsTopNav which no longer fits the number of sections. Structure:
//
//   ┌──────── SIDEBAR (260px) ────────┐
//   │ [Brand block]                   │
//   │ ─────────────────               │
//   │ [Nav item] · [Nav item] · …     │
//   │ ─────────────────               │
//   │ [Refresh public site]           │
//   │ [User profile + logout]         │
//   └─────────────────────────────────┘
//
// Mobile: sidebar is off-canvas. A small hamburger button appears in a
// slim top bar; clicking it slides the sidebar in as a drawer with a
// dimmed overlay.

const TABS = [
  { to: "/cms/pages", label: "General Pages", Icon: LayoutGrid },
  { to: "/cms/services", label: "Services", Icon: Briefcase },
  { to: "/cms/property-search", label: "Properties", Icon: Building2 },
  { to: "/cms/offices", label: "Offices", Icon: Building },
  { to: "/cms/insights", label: "Market Insights", Icon: TrendingUp },
  { to: "/cms/blogs", label: "Blogs", Icon: BookOpen },
  { to: "/cms/news", label: "News", Icon: Newspaper },
  { to: "/cms/press-coverage", label: "Press Coverage", Icon: Rss },
  { to: "/cms/careers", label: "Careers", Icon: Users },
  { to: "/cms/leadership", label: "Leadership", Icon: Award },
  { to: "/cms/brokers", label: "Brokers", Icon: Contact },
];

const PUBLIC_PATHS = [
  "/blogs",
  "/news",
  "/market-insights",
  "/property-search",
  "/for-sale-details",
  "/offices",
  "/careers",
  "/executive-leadership",
  // /aboutUs pulls the leaders collage and the brokers slideshow from
  // Firestore, so it must revalidate whenever either collection changes.
  "/aboutUs",
  ...SERVICES.map((s) => s.path),
  ...SITE_PAGES.map((p) => p.path),
  // Lists every blog/news/insight/listing/building/career URL.
  "/sitemap.xml",
];

function initialsFromUser(user) {
  const name = (
    user?.displayName ||
    user?.email ||
    ""
  ).trim();
  if (!name) return "?";
  const parts = name.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return name.slice(0, 1).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function CmsSidebar() {
  const currentUser = useSelector((state) => state.userObject);
  const [refreshing, setRefreshing] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-close the drawer when the viewport crosses back into desktop
  // territory — otherwise the drawer state can get stuck open when the
  // user resizes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      if (window.innerWidth >= 1024) setDrawerOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleRefreshPublicSite = async () => {
    const endpoint = process.env.REACT_APP_REVALIDATE_URL;
    if (!endpoint) {
      toast.error("Revalidate endpoint not configured.");
      return;
    }
    setRefreshing(true);
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await staffAuthHeaders()),
        },
        body: JSON.stringify({ paths: PUBLIC_PATHS }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      toast.success("Public site cache cleared.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to refresh public site.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logOutUser();
      // App.js listens to Firebase auth state and routes back to Login
      // automatically once signOut resolves — no manual navigate needed.
    } catch (err) {
      console.error(err);
      toast.error("Logout failed.");
      setIsLoggingOut(false);
    }
  };

  const initials = initialsFromUser(currentUser);
  const displayName =
    currentUser?.displayName || currentUser?.email || "Signed in";
  const displayEmail = currentUser?.email || "";
  const roleLine = Array.isArray(currentUser?.roles)
    ? currentUser.roles.join(", ")
    : "";

  const sidebarBody = (
    <>
      {/* Brand */}
      <div className="px-5 py-5 border-b border-white/10">
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">
          Leechiu Property Consultants
        </div>
        <div className="text-base font-semibold text-white mt-1 leading-tight">
          Website CMS
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3">
        {TABS.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setDrawerOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 pl-5 pr-4 py-2.5 text-sm font-medium border-l-[3px] transition-colors ${
                isActive
                  ? "border-leechiuOrange bg-white/5 text-white"
                  : "border-transparent text-white/70 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Icon className="w-4 h-4 shrink-0" />
            <span className="truncate">{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Utility + profile */}
      <div className="border-t border-white/10 p-3 space-y-3">
        <button
          type="button"
          onClick={handleRefreshPublicSite}
          disabled={refreshing}
          title="Clear the public site's cached HTML so newly-saved page settings appear immediately."
          className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 text-white text-xs font-semibold hover:bg-white/15 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
          />
          {refreshing ? "Refreshing…" : "Refresh public site"}
        </button>

        <div className="rounded-lg bg-white/5 p-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-leechiuOrange text-white text-xs font-bold flex items-center justify-center shrink-0">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-white truncate">
                {displayName}
              </div>
              {displayEmail && displayEmail !== displayName ? (
                <div className="text-[11px] text-white/60 truncate">
                  {displayEmail}
                </div>
              ) : null}
              {roleLine ? (
                <div className="text-[10px] text-white/50 truncate uppercase tracking-wide mt-0.5">
                  {roleLine}
                </div>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-white/80 hover:text-white hover:bg-white/10 border border-white/10 disabled:opacity-50"
          >
            <LogOut className="w-3.5 h-3.5" />
            {isLoggingOut ? "Signing out…" : "Log out"}
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile top bar with hamburger — only shows below lg */}
      <div className="lg:hidden sticky top-0 z-20 bg-leechiuBlue text-white flex items-center justify-between px-4 py-3">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          className="inline-flex items-center justify-center p-2 rounded-md hover:bg-white/10"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="text-sm font-semibold">Website CMS</div>
        <div className="w-9" aria-hidden="true" />
      </div>

      {/* Desktop fixed sidebar */}
      <aside className="hidden lg:flex fixed top-0 left-0 h-screen w-[260px] bg-leechiuBlue text-white flex-col z-30">
        {sidebarBody}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen ? (
        <>
          <div
            role="presentation"
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
          />
          <aside className="lg:hidden fixed top-0 left-0 h-screen w-[280px] bg-leechiuBlue text-white flex flex-col z-50 shadow-2xl">
            <div className="flex items-center justify-end p-2">
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close navigation"
                className="p-2 rounded-md hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {sidebarBody}
          </aside>
        </>
      ) : null}
    </>
  );
}
